import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Button as AntButton, Dropdown, MenuProps, Modal, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { ReactComponent as Add } from '../../../assets/add.svg';
import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg';
import { ReactComponent as Search } from '../../../assets/search.svg';
import Button from '../../../custom/button/button';
import SearchInput from '../../../custom/searchInput/searchInput';
import { useDeleteSpecializationMutation } from '../../../hooks/api/specialization/useDeleteSpecializationMutation';
import { Specialization, useGetSpecializationQuery } from '../../../hooks/api/specialization/useGetSpecializationQuery';
import { usePagination } from '../../../hooks/usePagination';
import { useSearchTerms } from '../../../hooks/useSearchTerms';
import { getAllPrograms } from '../../../requests';
import DeleteModalContent from '../../deleteModal/deleteModal';
import styles from '../styles.module.scss';

import AddSpecialization from './addSpecialization';

const SpecializationSetup = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [record, setRecord] = useState<Specialization>({} as Specialization);

  const { currentPage, onChange } = usePagination();
  const { searchTerm, handleSearch } = useSearchTerms();

  const { specializationData, isLoading, isError, errorMessage } = useGetSpecializationQuery();
  const { deleteSpecializationHandler, isDeleteLoading } = useDeleteSpecializationMutation(record?.id || 0, setOpenDelete);

  const { data: programs } = useQuery({
    queryKey: ['specialization-programs'],
    queryFn: () => getAllPrograms(1, 1000),
  });

  const programNameById = useMemo(() => {
    return new Map(programs?.data?.map((item) => [item?.id, item?.name]));
  }, [programs?.data]);

  const filteredData = specializationData
    ?.filter(
      (item) =>
        item?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        item?.code?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        programNameById.get(item?.academicProgramId)?.toLowerCase()?.includes(searchTerm.toLowerCase()),
    )
    ?.map((item) => ({ ...item, key: item.id }));

  const columns: ColumnsType<Specialization> = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
    },
    {
      key: 'code',
      title: 'Code',
      dataIndex: 'code',
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
    },
    {
      key: 'academicProgram',
      title: 'Academic Program',
      dataIndex: 'academicProgramId',
      render: (_, item) => programNameById.get(item?.academicProgramId) || item?.academicProgramId,
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'activeStatus',
      render: (_, { activeStatus }) => (activeStatus ? 'Active' : 'Inactive'),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, item) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Edit',
            onClick: () => {
              setRecord(item);
              setOpenEdit(true);
            },
          },
          {
            key: 'delete',
            label: 'Delete',
            onClick: () => {
              setRecord(item);
              setOpenDelete(true);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <AntButton type="text" icon={<Ellipsis />} />
          </Dropdown>
        );
      },
    },
  ];

  const handleShowAddModal = () => {
    setRecord({} as Specialization);
    setShowAddModal(true);
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>Error: {errorMessage}</div>;
  }

  return (
    <main>
      <section className="space-between">
        <h3>Specialization Setup</h3>
        <Button onClick={handleShowAddModal} iconBefore={<Add />} text="Setup" />
      </section>

      <section className={styles.card}>
        <div className={styles.inside}>
          <p>
            Showing 1-{filteredData?.length} of {specializationData?.length}
          </p>

          <div>
            {!showSearch && (
              <span>
                <Search onClick={() => setShowSearch((showSearch) => !showSearch)} />
              </span>
            )}

            {showSearch && <SearchInput value={searchTerm} onChange={handleSearch} />}
          </div>
        </div>

        <Table
          dataSource={filteredData}
          columns={columns}
          pagination={{
            position: ['bottomCenter'],
            current: currentPage,
            total: filteredData?.length,
            onChange: onChange,
            pageSize: 10,
          }}
        />
      </section>

      <Modal
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        centered
        title="Create Specialization"
        footer={null}
      >
        <AddSpecialization handleClose={() => setShowAddModal(false)} />
      </Modal>

      <Modal open={openEdit} onCancel={() => setOpenEdit(false)} centered title="Edit Specialization" footer={null}>
        <AddSpecialization record={record} handleClose={() => setOpenEdit(false)} isEdit />
      </Modal>

      <Modal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        centered
        title="Delete Specialization Setup"
        footer={null}
      >
        <DeleteModalContent
          isLoading={isDeleteLoading}
          handleCloseModal={() => setOpenDelete(false)}
          handleSubmit={deleteSpecializationHandler}
          title="Specialization"
        />
      </Modal>
    </main>
  );
};

export default SpecializationSetup;
